import { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalContent, ModalHeader } from "@heroui/react";
import { ContactType, ContractContact, RentalContract } from 'shared/types/rental-contracts';
import { EditableSelectComponent } from 'components/index';
import SearchComponent, { SearchMode } from 'shared/components/searchBar/components/searchComponent';
import { ResourceType } from 'shared/types/resource';
import ButtonNew from 'components/buttonNew/buttonNew';
import { PlugsConnected } from '@phosphor-icons/react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';

interface ConnectContactModalProps {
    isOpen: boolean;
    onClose: () => void;
    contract: RentalContract;
    linkContactToContract: (contact: ContractContact) => void;
}

const ConnectContactModal = ({ isOpen, onClose, contract, linkContactToContract }: ConnectContactModalProps) => {
    const [linkedContact, setLinkedContact] = useState<ContractContact>({ contactType: null, contract: null, resource: null, resourceId: null });
    const { session } = useSession();

    useEffect(() => {
        setLinkedContact((prev: any) => ({
            ...prev,
            contract: contract,
        }));
    }, [contract]);

    const handleLinkContact = async () => {
        if(!linkedContact.contactType || !linkedContact.resourceId) {
            toast.error("Gelieve een contacttype en contact te selecteren");
            return;
        }

        if(!linkedContact.contract) {
            toast.error("Het achterliggende contract is niet meer gelinkt. Je moet je modal even sluiten en opnieuw openen.");
            return;
        }

        try {
            let res = await fetchWithAuth(
                `${API_URL}/rental/link-contact`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ linkedContact: linkedContact }),
                  },
                session,
            );

            const contact = await res.json();

            if (res.ok) {
                linkContactToContract(contact);
                toast.success("Contact werd gekoppeld!");
                handleClose();
            } else {
                toast.error("Er is iets misgegaan bij het koppelen van het contact");
            }
        } catch (error) {
            toast.error("Er is iets misgegaan bij het koppelen van het contact");
        }
    }

    const handleSelectResource = (resource: any) => {
        setLinkedContact((prev: any) => ({
            ...prev,
            resourceId: resource.id,
            resource: resource,
        }));
    }

    const contactTypeOptions = Object.values(ContactType).map((value) => ({
        value: value,
        label: value,
    }));

    const handleClose = () => {
        setLinkedContact({ contactType: null, contract: contract, resource: null, resourceId: null });
        onClose();
    }

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                size="4xl"
                shouldBlockScroll={true}
                scrollBehavior="outside"
                backdrop="blur"
                className="p-8 "
            >
                <ModalContent className="flex flex-col gap-4 font-normal text-neutral-primary">
                    <ModalHeader className="flex flex-row items-center justify-between p-0 font-normal">
                        <p>Koppel contact aan huurcontract</p>
                        <ButtonNew
                            icon={<PlugsConnected />}
                            label="Koppel contact"
                            onClick={handleLinkContact}
                            variant="primaryMoss"
                        />
                    </ModalHeader>
                    <ModalBody className="grid gap-5 gap-y-8 px-0 sm:grid-cols-2">
                        <EditableSelectComponent
                            label="Type contact"
                            editMode={true}
                            value={
                                linkedContact.contactType as string
                            }
                            options={contactTypeOptions}
                            onChange={(value: string) =>
                                setLinkedContact((prev: any) => ({
                                    ...prev,
                                    contactType: value,
                                }))
                            }
                        />
                        <SearchComponent
                            label="Zoek contact of bedrijf"
                            onSelect={(resource: any) =>
                                handleSelectResource(resource)
                            }
                            mode={SearchMode.SEARCH_AND_PRESENT}
                            editMode={true}
                            resourceFilter={[ResourceType.CONTACT, ResourceType.COMPANY]}
                        />
                    </ModalBody>

                </ModalContent>
            </Modal>
        </>
    );
};

export default ConnectContactModal;
